export function roundToNearest(value, precision) {
  return Math.round(value / precision) * precision;
}

export function roundToNearestFixed(value, precision) {
  const roundedValue = roundToNearest(value, precision);
  const numDecimals = Math.ceil(-Math.log10(precision));
  return roundedValue.toFixed(numDecimals);
}

export function roundToNearestFixedRange(value, minPrecision, maxPrecision) {
  const minDecimals = Math.ceil(-Math.log10(minPrecision));
  const maxDecimals = Math.ceil(-Math.log10(maxPrecision));
  const roundedValue = roundToNearest(value, maxPrecision);
  const roundedValueTrimmed = roundedValue.toFixed(Math.max(1, maxDecimals)).replace(/0*$/, "");
  const actualDecimals = roundedValueTrimmed.split(".")[1].length;
  const finalDecimals = Math.min(maxDecimals, Math.max(minDecimals, actualDecimals));
  return roundedValue.toFixed(finalDecimals);
}

export function roundDownToNearestFixed(value, precision) {
  const roundedValue = Math.floor(value / precision) * precision;
  const numDecimals = Math.ceil(-Math.log10(precision));
  return roundedValue.toFixed(numDecimals);
}

export function roundUpToNearestFixed(value, precision) {
  const roundedValue = Math.ceil(value / precision) * precision;
  const numDecimals = Math.ceil(-Math.log10(precision));
  return roundedValue.toFixed(numDecimals);
}

export function sumBy(items, callback) {
  return items.reduce((sum, item) => sum + callback(item), 0);
}

export function unique(items) {
  let result = [];
  items.forEach(item => {
    if (!result.includes(item))
      result.push(item);
  });
  return result;
}

export function uniqueBy(items, callback) {
  let callbackResults = items.map(callback);
  let callbackResults_unique = unique(callbackResults);
  let pairs = items.map((item, index) => ({ item, callbackResult: callbackResults[index] }));
  return callbackResults_unique.map(callbackResult => pairs.find(pair => pair.callbackResult == callbackResult));
}

export function groupBy(items, callback) {
  return items.reduce((acc, item) => {
    let key = callback(item);
    if (acc[key]) acc[key].push(item);
    else acc[key] = [ item ];
    return acc;
  }, {});
}

export function keyBy(items, keyCallback) {
  let map = {};
  items.forEach(item => {
    let mapKey = keyCallback(item);
    map[mapKey] = item;
  });
  return map;
}

export function sortBy(items, ... callbacks) {
  return items.slice().sort((itemA, itemB) => {
    for (let i = 0; i < callbacks.length; i++) {
      let callback = callbacks[i];
      let valueA = callback(itemA);
      let valueB = callback(itemB);
      let comparisonResult = (() => {
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      })();
      if (comparisonResult != 0) {
        return comparisonResult;
      }
    }
    return 0;
  });
}

export function minBy(items, callback) {
  if (items.length === 0)
    return null;

  let minItem = items[0];
  let minValue = callback(minItem);
  for (let i = 1; i < items.length; i++) {
    let value = callback(items[i]);
    if (value < minValue) {
      minItem = items[i];
      minValue = value;
    }
  }
  return minItem;
}

export function maxBy(items, callback) {
  if (items.length === 0)
    return null;

  let maxItem = items[0];
  let maxValue = callback(maxItem);
  for (let i = 1; i < items.length; i++) {
    let value = callback(items[i]);
    if (value > maxValue) {
      maxItem = items[i];
      maxValue = value;
    }
  }
  return maxItem;
}

export function pickBy(object, callback) {
  let result = {};
  let keys = Object.keys(object);
  keys.forEach(key => {
    let value = object[key];
    if (callback(value, key))
      result[key] = value;
  });
  return result;
}

export function mapValues(obj, callback) {
  let result = {};
  let entries = Object.entries(obj);
  entries.forEach(([key, value]) => {
    result[key] = callback(value);
  });
  return result;
}

export function without(items, ... excludedItems) {
  return items.filter(item => !excludedItems.includes(item));
}

export function intersection(... arrays) {
  let result = [];
  arrays.forEach(array => {
    let filtered = array.filter(value => arrays.every(otherArray => otherArray.includes(value)));
    result.push(... filtered);
  });
  result = unique(result);
  return result;
}

export function find(items, callback) {
  return items.find(callback);
}

export function findFirst(items, callback) {
  return items.find(callback);
}

export function findLast(items, callback) {
  let itemsCopy = items.slice();
  itemsCopy.reverse();
  return itemsCopy.find(callback);
}

export function last(items) {
  return items[items.length - 1];
}

export function chunk(items, chunkSize) {
  let result = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    result.push(items.slice(i, i + chunkSize))
  }
  return result;
}

export function stringFindIndex(string, callback) {
  for (let i = 0; i < string.length; i++) {
    if (callback(string[i], i))
      return i;
  }
  return null;
}

export function range(start, end, step = 1) {
  let result = [];
  for (let i = start; i < end; i += step) {
    result.push(i);
  }
  return result;
}

export function swap(items, indexA, indexB) {
  let temp = items[indexA];
  items[indexA] = items[indexB];
  items[indexB] = temp;
}

export function arrayMove(array, fromIndex, toIndex) {
  var element = array[fromIndex];
  array.splice(fromIndex, 1);
  array.splice(toIndex, 0, element);
}

export function hasMatchingItemsBy(items, callback) {
  return items.some(item1 => {
    return items.some(item2 => {
      return (item1 != item2 && callback(item1, item2));
    });
  });
}

export function padStart(string, minLen, padChar) {
  let result = string.toString();
  while (result.length < minLen) {
    result = padChar + result;
  }
  return result;
}

export function map_dropNulls(items, callback) {
  return items.map(callback).filter(item => item != null);
}

// https://youmightnotneed.com/lodash#set
export function set(obj, path, value) {
  let pathArray = path.match(/([^[.\]])+/g);
  pathArray.reduce((acc, key, i) => {
    if (acc[key] === undefined) {
      if (isNaN(pathArray[i + 1])) {
        acc[key] = {};
      } else {
        acc[key] = [];
      }
    }
    if (i === pathArray.length - 1) acc[key] = value;
    return acc[key];
  }, obj);
}